<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center planner-template-status-modal">
    <div class="flex items-center justify-center min-h-screen text-center">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- Modal panel -->
      <div class="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-5xl w-full">
        <div class="bg-white px-6 pt-6 pb-5 sm:p-8 sm:pb-6">
          <div class="flex justify-between items-start">
            <div>
              <h3 class="text-xl leading-6 font-semibold text-gray-900">
                Planner Template Status
              </h3>
              <p class="mt-2 text-sm text-gray-600">
                Prepare templates for planners
              </p>
            </div>

            <button @click="close" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="mt-6">
            <div class="bg-gray-50 p-4 rounded-md">
              <div class="flex mb-2">
                <span class="text-sm font-medium text-gray-700 flex-1">Planner Type</span>
                <span class="text-sm font-medium text-gray-700 w-32 text-center">Status</span>
                <span class="text-sm font-medium text-gray-700 w-32 text-center">Action</span>
              </div>
              
              <div class="space-y-2 max-h-[50vh] overflow-y-auto">
                <div 
                  v-for="(status, type) in plannerStatus" 
                  :key="type"
                  class="flex items-center p-2 rounded-md"
                  :class="status.exists ? 'bg-green-50' : 'bg-red-50'"
                >
                  <span class="text-sm flex-1">{{ type }}</span>
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full w-24 text-center"
                    :class="status.exists ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                  >
                    {{ status.exists ? 'Available' : 'Missing' }}
                  </span>
                  <div class="w-32 text-center">
                    <button
                      v-if="!status.exists"
                      @click="preparePlannerTemplate(type)"
                      :disabled="status.loading"
                      class="px-4 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center w-20 mx-auto"
                    >
                      <svg v-if="status.loading" class="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {{ status.loading ? 'Creating...' : 'Create' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="bg-gray-50 px-6 py-4 sm:px-8 flex justify-between">
          <button
            @click="prepareAllMissingTemplates"
            :disabled="isAnyLoading || !hasMissingTemplates"
            class="inline-flex justify-center rounded-md border border-primary shadow-sm px-5 py-2.5 bg-primary text-white text-base font-medium hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="isAllLoading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Create All Missing
          </button>
          <button
            @click="close"
            class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-5 py-2.5 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, ref, onMounted, onUnmounted } from 'vue';

export interface PlannerStatus {
  exists: boolean;
  loading: boolean;
}

export default defineComponent({
  name: 'PlannerTemplateStatusModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    forecastId: {
      type: String,
      required: true
    },
    plannerStatus: {
      type: Object as PropType<Record<string, PlannerStatus>>,
      required: true
    }
  },
  emits: ['close', 'prepare-template', 'prepare-all-missing'],
  setup(props, { emit }) {
    const isAllLoading = ref(false);
    
    const close = () => {
      emit('close');
    };

    const preparePlannerTemplate = (reportType: string) => {
      emit('prepare-template', reportType);
    };
    
    const prepareAllMissingTemplates = () => {
      isAllLoading.value = true;
      emit('prepare-all-missing');
      // Note: The parent component should handle resetting isAllLoading when complete
      setTimeout(() => {
        isAllLoading.value = false;
      }, 5000); // Fallback timeout in case parent doesn't handle it
    };
    
    const isAnyLoading = computed(() => {
      return Object.values(props.plannerStatus).some(status => status.loading) || isAllLoading.value;
    });
    
    const hasMissingTemplates = computed(() => {
      return Object.values(props.plannerStatus).some(status => !status.exists);
    });
    
    // Event listener for when all templates are created
    const handleAllTemplatesCreated = () => {
      isAllLoading.value = false;
    };
    
    // Set up event listener when component is mounted
    onMounted(() => {
      const modalElement = document.querySelector('.planner-template-status-modal');
      if (modalElement) {
        modalElement.addEventListener('all-templates-created', handleAllTemplatesCreated);
      }
    });
    
    // Clean up event listener when component is unmounted
    onUnmounted(() => {
      const modalElement = document.querySelector('.planner-template-status-modal');
      if (modalElement) {
        modalElement.removeEventListener('all-templates-created', handleAllTemplatesCreated);
      }
    });

    return {
      close,
      preparePlannerTemplate,
      prepareAllMissingTemplates,
      isAnyLoading,
      isAllLoading,
      hasMissingTemplates
    };
  }
});
</script>